import React, { Component } from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import FoundationLayout from '../components/layout/FoundationLayout';
import IITFoundationStudentDashboardSection1 from '../components/IITDashboard/IITFoundationStudentDashboardSection1';
import FoundationAttemptAnalysis from '../components/test/FoundationAttemptAnalysis';
import section1DummyData from '../components/IITDashboard/section1DummyData';
import { getFoundationStudent } from '../foundationSession';

const resolveStudentId = (props) => {
    const nav = props.location && props.location.state && (props.location.state || {}).student;
    if (nav && nav.id) return nav.id;
    const sess = getFoundationStudent();
    return sess && sess.id ? sess.id : null;
};

/**
 * IIT Foundation dashboard. Keeps the full card layout, but every number starts
 * at 0 (the dummy fixtures are zeroed) and real metrics from getFoundationDashboard
 * are merged in where a data source exists (accuracy, tests, questions, subjects).
 * The Mock Test quick-action is dropped — the portal has no mock tests.
 */
const FOUNDATION_STUDENT = gql`
  query($id: ID!) { getFoundationStudent(id: $id) { id name class program school mobile_mask email_mask status } }
`;

const letterGrade = (acc) =>
    acc >= 90 ? "A+" : acc >= 80 ? "A" : acc >= 70 ? "B" : acc >= 60 ? "C" : acc >= 50 ? "D" : acc > 0 ? "E" : "—";
const FOUNDATION_DASHBOARD = gql`
  query($rid: Int!) {
    getFoundationDashboard(registration_id: $rid) {
      tests_taken questions_attempted questions_correct accuracy time_spent_seconds
      notes_time_seconds notes_completed notes_total error_questions
      subjects { subject attempted correct accuracy avg_time_seconds }
      recent { attempt_id chapter_name score total_questions submitted_at }
    }
  }
`;

const SUBJECT_ICON = {
    Mathematics: "fas fa-square-root-alt", Physics: "fas fa-atom",
    Chemistry: "fas fa-flask", Biology: "fas fa-dna", Science: "fas fa-microscope",
};
const fmtDay = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" }); }
    catch (e) { return ""; }
};

// Recursively zero every number in the dummy fixtures — keeps labels/structure,
// drops the fake values so nothing static is shown.
function zeroNumbers(v) {
    if (Array.isArray(v)) return v.map(zeroNumbers);
    if (v && typeof v === 'object') {
        const o = {};
        for (const k in v) o[k] = zeroNumbers(v[k]);
        return o;
    }
    return typeof v === 'number' ? 0 : v;
}

function buildData(dash, student) {
    const data = zeroNumbers(section1DummyData);

    // drop everything mock-test related — the portal has no mock tests.
    if (data.quickActions && Array.isArray(data.quickActions.cards)) {
        data.quickActions.cards = data.quickActions.cards.filter((c) => c.id !== 'mock');
    }
    if (data.examAnalysis && Array.isArray(data.examAnalysis.exams)) {
        data.examAnalysis.exams = data.examAnalysis.exams.filter((e) => e.type !== 'Mock');
    }
    // Weekly Performance has no real data source — drop it rather than show zeros.
    data.weekly = null;

    // real header highlights; drop the static Program/Rank strings that zeroing
    // couldn't clear (they were text, not numbers). School is not shown.
    data.highlights = (data.highlights || []).filter((h) => h.id !== 'school').map((h) => {
        if (h.id === 'class') return { ...h, value: (student && student.class) || "—" };
        if (h.id === 'program') return { ...h, value: (student && student.program) || "IIT Foundation" };
        if (h.id === 'rank') return { ...h, value: "—", hint: "not ranked yet" };
        return h;
    });

    // grade block — derived from real accuracy (—/0 until tests are taken)
    if (data.grade) {
        const acc = dash ? dash.accuracy : 0;
        data.grade = {
            ...data.grade,
            grade: letterGrade(acc),
            percentage: acc,
            hint: dash && dash.tests_taken ? "across your tests" : "no tests yet",
        };
    }

    // Always feed real (or zero) values so the static fixture strings never flash.
    const d = dash || {};
    const notesTotal = Number(d.notes_total) || 0;
    const notesDone = Number(d.notes_completed) || 0;
    const notesPct = notesTotal ? Math.round((notesDone / notesTotal) * 100) : 0;
    const notesMins = Math.round((d.notes_time_seconds || 0) / 60);
    const errQ = Number(d.error_questions) || 0;

    // KPI cards
    data.kpis.cards = (data.kpis.cards || []).map((c) => {
        if (c.id === 'score' || c.id === 'accuracy') {
            return { ...c, value: d.accuracy || 0, progress: d.accuracy || 0, caption: `${d.questions_correct || 0} of ${d.questions_attempted || 0} correct` };
        }
        if (c.id === 'tests') {
            return { ...c, value: d.tests_taken || 0, caption: `${Math.round((d.time_spent_seconds || 0) / 60)} min practised` };
        }
        if (c.id === 'syllabus') {
            return { ...c, value: notesPct, suffix: '%', progress: notesPct, caption: `${notesDone} of ${notesTotal} topics read` };
        }
        return { ...c, caption: '' };
    });

    // Quick Actions — notes progress + error analysis (dynamic)
    data.quickActions.cards = (data.quickActions.cards || []).map((c) => {
        if (c.id === 'continue') {
            return {
                ...c,
                title: 'Notes Progress',
                description: 'Keep reading — your progress is tracked automatically',
                metricValue: String(notesDone),
                metricLabel: 'topics completed',
                badge: `${notesPct}% done`,
                badgeTone: 'soft',
                progress: notesPct,
                progressLabel: `${notesDone} of ${notesTotal} topics · ${notesMins} min read`,
                cta: 'Open Notes',
            };
        }
        if (c.id === 'errors') {
            return {
                ...c,
                title: 'Error Analysis',
                description: 'Re-attempt the questions you got wrong',
                metricValue: String(errQ),
                metricLabel: errQ === 1 ? 'question to review' : 'questions to review',
                badge: errQ > 0 ? 'Needs attention' : 'All clear',
                badgeTone: errQ > 0 ? 'soft' : 'new',
                progress: 0,
                progressLabel: errQ > 0 ? `${errQ} wrong so far` : 'No wrong answers yet',
                cta: 'Start Error Exam',
            };
        }
        return c;
    });

    // Add a "Create Exam" quick action (build a custom exam by subjects + topics).
    if (data.quickActions && Array.isArray(data.quickActions.cards) &&
        !data.quickActions.cards.some((c) => c.id === 'create')) {
        data.quickActions.cards.push({
            id: 'create',
            theme: 'blue',
            icon: 'fas fa-plus-circle',
            title: 'Create Exam',
            description: 'Build your own test from any subjects and topics',
            metricValue: 'New',
            metricLabel: 'custom exam',
            badge: 'Your choice',
            badgeTone: 'new',
            hideProgress: true,
            cta: 'Create Exam',
        });
    }

    if (dash) {
        // Subject Mastery — real per-subject numbers
        if (dash.subjects && dash.subjects.length) {
            data.mastery.subjects = dash.subjects.map((s) => ({
                id: s.subject,
                subject: s.subject,
                icon: SUBJECT_ICON[s.subject] || "fas fa-book",
                questionsSolved: s.attempted,
                avgTimePerQuestion: s.avg_time_seconds,
                accuracy: s.accuracy,
                mastery: Math.round(s.accuracy),
            }));
            data.mastery.badge = `${dash.subjects.length} subjects`;
        }

        // Exam Analysis — real recent attempts
        if (dash.recent && dash.recent.length) {
            data.examAnalysis.exams = dash.recent.map((r, i) => {
                const pct = r.total_questions ? Math.round((r.score / r.total_questions) * 1000) / 10 : 0;
                return {
                    id: `a${r.attempt_id}`,
                    attemptId: r.attempt_id,
                    name: r.chapter_name || "Chapter Test",
                    type: "Chapter",
                    date: fmtDay(r.submitted_at),
                    score: r.score,
                    total: r.total_questions,
                    percentage: pct,
                    accuracy: pct,
                    rank: 0,
                    outOf: 0,
                };
            });
        }
    }
    return data;
}

const ACTION_ROUTES = {
    continue: '/iit-foundation/short-notes',
    errors: '/student/iit-foundation-course/error-exams',
    create: '/iit-foundation/create-exam',
};

class IITFoundationStudentDashboard extends Component {
    state = { analysisAttemptId: null };

    handleAction = (id) => {
        const dest = ACTION_ROUTES[id];
        if (!dest) return;
        if (this.props.history) this.props.history.push(dest);
        else window.location.assign(dest);
    };

    // "View Analysis" on a recent test → open the real, data-backed analysis
    openAnalysis = (exam) => {
        const id = exam && (exam.attemptId != null ? exam.attemptId
            : (typeof exam.id === 'string' ? Number(exam.id.replace(/^a/, '')) : null));
        if (id) this.setState({ analysisAttemptId: id });
    };
    closeAnalysis = () => this.setState({ analysisAttemptId: null });

    render() {
        const navState = (this.props.location && this.props.location.state) || {};
        const navStudent = navState.student || getFoundationStudent() || null;
        const student = (this.props.foundationStudent || {}).getFoundationStudent || navStudent || null;

        let headerStudent = null;
        if (student && student.name) {
            const cls = student.class || "";
            headerStudent = {
                id: student.id,
                name: student.name,
                firstName: String(student.name).trim().split(" ")[0],
                program: cls,
                tagline: cls,
            };
        }

        const dash = (this.props.foundationDashboard || {}).getFoundationDashboard || null;
        const data = buildData(dash, student);

        return (
            <FoundationLayout student={student}>
                <IITFoundationStudentDashboardSection1
                    student={headerStudent}
                    data={data}
                    onAction={this.handleAction}
                    onViewAnalysis={this.openAnalysis}
                />
                {this.state.analysisAttemptId ? (
                    <FoundationAttemptAnalysis
                        attemptId={this.state.analysisAttemptId}
                        onClose={this.closeAnalysis}
                    />
                ) : null}
            </FoundationLayout>
        );
    }
}

export default graphql(FOUNDATION_STUDENT, {
    name: "foundationStudent",
    skip: (props) => !resolveStudentId(props),
    options: (props) => ({ variables: { id: resolveStudentId(props) }, fetchPolicy: "cache-and-network" }),
})(
    graphql(FOUNDATION_DASHBOARD, {
        name: "foundationDashboard",
        skip: (props) => !resolveStudentId(props),
        options: (props) => ({ variables: { rid: Number(resolveStudentId(props)) }, fetchPolicy: "cache-and-network" }),
    })(IITFoundationStudentDashboard)
);
